import { useNavigate, useLocation } from "react-router-dom";
import { Award, Zap, User } from "lucide-react";

/**
 * Fixed bottom navigation (mobile-first, always visible).
 * Suno-style: 3 icons locked to the viewport bottom even while scrolling.
 * `onProfileClick` opens the profile modal on the current page (works on /game).
 * On other pages (e.g. /friends, /room), tapping Profile navigates to /game which owns the modal.
 */
export default function BottomNav({ onProfileClick }) {
  const navigate = useNavigate();
  const location = useLocation();

  const items = [
    {
      key: "friends",
      label: "Friends",
      icon: Zap,
      color: "#00F0FF",
      testid: "bottom-nav-friends-btn",
      active: location.pathname.startsWith("/friends") || location.pathname.startsWith("/room"),
      onClick: () => navigate("/friends"),
    },
    {
      key: "darepass",
      label: "DarePass",
      icon: Award,
      color: "#EAEB18",
      testid: "bottom-nav-darepass-btn",
      active: location.pathname.startsWith("/pricing"),
      onClick: () => navigate("/pricing"),
    },
    {
      key: "profile",
      label: "Profile",
      icon: User,
      color: "#FF007F",
      testid: "bottom-nav-profile-btn",
      active: false,
      onClick: () => {
        if (onProfileClick) return onProfileClick();
        navigate("/game", { state: { openProfile: true } });
      },
    },
  ];

  return (
    <>
      {/* Spacer so scrollable content doesn't hide behind the fixed bar */}
      <div className="h-20" aria-hidden="true" />
      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#00F0FF]/25 bg-[#050505]/95 backdrop-blur-md"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-3xl mx-auto grid grid-cols-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                onClick={it.onClick}
                data-testid={it.testid}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${
                  it.active
                    ? "bg-white/[0.03]"
                    : "hover:bg-white/[0.02]"
                }`}
                aria-label={it.label}
              >
                <Icon
                  size={20}
                  strokeWidth={2.25}
                  color={it.active ? it.color : "#94A3B8"}
                />
                <span
                  className="text-[10px] uppercase tracking-[0.2em] font-heading font-bold"
                  style={{ color: it.active ? it.color : "#94A3B8" }}
                >
                  {it.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
